import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Paper,
  Breadcrumbs,
  Link,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  SwapHoriz as TransferIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Note as NoteIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getSeekingTransferDetail,
  updateSeekingTransfer,
  updateSeekingTransferChapter,
  addSeekingTransferChapter,
  transferSeekingTransferChapter,
  reopenSeekingTransferChapter,
  undoSeekingTransferContact,
  pauseTransferRequest,
  unpauseTransferRequest,
  getCannedNotes,
  createNote,
} from '../../../endpoints/seekingTransfer';
import ChapterSelector from '../../../Components/ChapterSelector';
import ConfirmationDialog from '../../../Components/ConfirmationDialog';
import AlertItem from '../../../Components/AlertItem';
import {
  SeekingTransferDetail as SeekingTransferDetailType,
  SeekingTransferUpdate,
  SeekingTransferChapterUpdate,
  PauseTransferRequest,
  CannedNote,
  CreateNoteRequest,
} from '../../../types/seekingTransfer';

const CHAPTER_STATUS_OPTIONS = [
  { value: '894013f6f9a24710b850736f07438594', label: 'Open', color: 'info' },
  {
    value: '6190df3b5f14416ca50a04c8ee443026',
    label: 'Removed',
    color: 'danger',
  },
  {
    value: '155a713f82854c07819a533a22ebe158',
    label: 'Transferred',
    color: 'success',
  },
];

const STATUS_OPTIONS = [
  { value: '60baf6c89e6f4343900e4dba8188548d', label: 'Open', color: 'info' },
  {
    value: '35005181463e47b18c000565577cbacf',
    label: 'Paused',
    color: 'warning',
  },
  {
    value: '92f6b54d6bc64442a27beadc1d32e311',
    label: 'Removed',
    color: 'danger',
  },
  {
    value: '2ca52f70ef7c49d9851d6205128aebf0',
    label: 'Completed',
    color: 'success',
  },
];

export default function SeekingTransferDetail(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // State management
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<SeekingTransferDetailType>>(
    {}
  );
  const [isAddingChapter, setIsAddingChapter] = useState(false);
  const [selectedChapterToAdd, setSelectedChapterToAdd] = useState<string>('');
  const [isAddingAttempt, setIsAddingAttempt] = useState(false);
  const [currentAttemptNumber, setCurrentAttemptNumber] = useState<1 | 2 | 3>(
    1
  );
  const [selectedChapterForAttempt, setSelectedChapterForAttempt] = useState<
    string | null
  >(null);
  const [attemptDate, setAttemptDate] = useState<string>('');
  const [attemptSuccessful, setAttemptSuccessful] = useState<boolean>(false);
  const [showTransferConfirm, setShowTransferConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [selectedChapterForAction, setSelectedChapterForAction] =
    useState<string>('');
  const [transferDate, setTransferDate] = useState<string>('');
  const todayYYYYMMDD = dayjs().format('YYYY-MM-DD');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedActionsChapterId, setExpandedActionsChapterId] = useState<
    string | null
  >(null);
  // Pause state
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [pauseDuration, setPauseDuration] = useState<
    '30' | '60' | '90' | 'other'
  >('30');
  const [pauseCustomDate, setPauseCustomDate] = useState<dayjs.Dayjs | null>(
    null
  );
  const [pendingPauseDate, setPendingPauseDate] = useState<string | null>(null); // For edit mode pause workflow
  const [isPauseFromEdit, setIsPauseFromEdit] = useState(false); // Track if pause dialog opened from edit mode
  // Notes state
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [selectedChapterForNote, setSelectedChapterForNote] = useState<
    string | null
  >(null);
  const [cannedNotes, setCannedNotes] = useState<CannedNote[]>([]);
  const [selectedCannedNoteType, setSelectedCannedNoteType] =
    useState<string>('all');
  const [selectedCannedNote, setSelectedCannedNote] = useState<string | null>(
    null
  );
  const [noteType, setNoteType] = useState<'canned' | 'other'>('canned');
  const [otherNoteText, setOtherNoteText] = useState('');
  const [pendingContactAttempt, setPendingContactAttempt] = useState<{
    chapterId: string;
    attempt: 'one' | 'two' | 'three';
    date: string;
    successful: boolean;
  } | null>(null);

  // API hooks
  const {
    data: transferDetail,
    isLoading,
    refetch,
  } = useApiGet<SeekingTransferDetailType>(
    ['seeking-transfer-detail', id],
    getSeekingTransferDetail,
    {
      enabled: !!id,
      retry: 1,
    },
    id
  );

  const { mutate: updateTransfer, isPending: isUpdating } = useApiSend(
    (params: { id: string; data: SeekingTransferUpdate }) =>
      updateSeekingTransfer(params.id, params.data),
    () => {
      setIsEditing(false);
      setSuccessMessage('Transfer request updated successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      setErrorMessage('Failed to update transfer request. Please try again.');
      console.error('Error updating transfer:', error);
    }
  );

  const { mutate: updateChapter, isPending: isUpdatingChapter } = useApiSend(
    (params: {
      id: string;
      chapterId: string;
      data: SeekingTransferChapterUpdate;
    }) =>
      updateSeekingTransferChapter(params.id, params.chapterId, params.data),
    () => {
      setSuccessMessage('Chapter updated successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Failed to update chapter. Please try again.');
      }
      console.error('Error updating chapter:', error);
    }
  );

  const { mutate: addChapter, isPending: isAddingChapterPending } = useApiSend(
    (params: { chapter_id: string }) => addSeekingTransferChapter(id!, params),
    () => {
      setSelectedChapterToAdd('');
      setIsAddingChapter(false);
      setSuccessMessage('Chapter added successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Failed to add chapter. Please try again.');
      }
      console.error('Error adding chapter:', error);
    }
  );

  const { mutate: transferChapter, isPending: isTransferringChapter } =
    useApiSend(
      (params: {
        id: string;
        chapterId: string;
        data: { transfer_date: string };
      }) =>
        transferSeekingTransferChapter(
          params.id,
          params.chapterId,
          params.data
        ),
      () => {
        setTransferDate('');
        setSelectedChapterForAction('');
        setShowTransferConfirm(false);
        setSuccessMessage('Chapter transferred successfully!');
        refetch();
        setTimeout(() => setSuccessMessage(''), 3000);
      },
      (error: Error) => {
        if (error.message) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('Failed to transfer chapter. Please try again.');
        }
        console.error('Error transferring chapter:', error);
      }
    );

  const { mutate: reopenChapter, isPending: isReopeningChapter } = useApiSend(
    (params: { id: string; chapterId: string }) =>
      reopenSeekingTransferChapter(params.id, params.chapterId),
    () => {
      setSuccessMessage('Chapter reopened successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Failed to reopen chapter. Please try again.');
      }
      console.error('Error reopening chapter:', error);
    }
  );

  const { mutate: undoContact, isPending: isUndoingContact } = useApiSend(
    (params: { id: string; chapterId: string }) =>
      undoSeekingTransferContact(params.id, params.chapterId),
    () => {
      setSuccessMessage('Contact made undone successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Failed to undo contact made. Please try again.');
      }
      console.error('Error undoing contact:', error);
    }
  );

  const { mutate: pauseTransfer, isPending: isPausing } = useApiSend(
    (params: { id: string; data: PauseTransferRequest }) =>
      pauseTransferRequest(params.id, params.data),
    () => {
      setShowPauseDialog(false);
      setPauseDuration('30');
      setPauseCustomDate(null);
      setSuccessMessage('Transfer request paused successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Failed to pause transfer request. Please try again.');
      }
      console.error('Error pausing transfer:', error);
    }
  );

  const { mutate: unpauseTransfer, isPending: isUnpausing } = useApiSend(
    (params: { id: string }) => unpauseTransferRequest(params.id),
    () => {
      setSuccessMessage('Transfer request unpaused successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(
          'Failed to unpause transfer request. Please try again.'
        );
      }
      console.error('Error unpausing transfer:', error);
    }
  );

  const { mutate: createNoteMutation, isPending: isCreatingNote } = useApiSend(
    (params: { id: string; data: CreateNoteRequest }) =>
      createNote(params.id, params.data),
    () => {
      setShowNoteDialog(false);
      setSelectedChapterForNote(null);
      setSelectedCannedNote(null);
      setNoteType('canned');
      setOtherNoteText('');
      setSuccessMessage('Note added successfully!');

      // If there's a pending contact attempt, submit it now
      if (pendingContactAttempt && id) {
        const updateData: SeekingTransferChapterUpdate = {};
        if (pendingContactAttempt.attempt === 'one') {
          updateData.contact_attempt_one = pendingContactAttempt.date;
          updateData.contact_one_successful = pendingContactAttempt.successful;
        } else if (pendingContactAttempt.attempt === 'two') {
          updateData.contact_attempt_two = pendingContactAttempt.date;
          updateData.contact_two_successful = pendingContactAttempt.successful;
        } else if (pendingContactAttempt.attempt === 'three') {
          updateData.contact_attempt_three = pendingContactAttempt.date;
          updateData.contact_three_successful =
            pendingContactAttempt.successful;
        }

        updateChapter({
          id,
          chapterId: pendingContactAttempt.chapterId,
          data: updateData,
        });

        // Reset attempt dialog
        setAttemptDate('');
        setAttemptSuccessful(false);
        setIsAddingAttempt(false);
        setSelectedChapterForAttempt(null);
        setPendingContactAttempt(null);
      }

      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Failed to create note. Please try again.');
      }
      console.error('Error creating note:', error);
    }
  );

  // Event handlers
  const handleEdit = useCallback(() => {
    if (transferDetail) {
      console.log('Setting edit data:', {
        status_id: transferDetail.status_id,
        comment: transferDetail.comment,
      });
      setEditData({
        status_id: transferDetail.status_id,
        comment: transferDetail.comment,
      });
      setIsEditing(true);
    }
  }, [transferDetail]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditData({});
    setPendingPauseDate(null); // Clear any pending pause date
    setIsPauseFromEdit(false);
  }, []);

  const isSelf = useCallback(() => {
    return localStorage.getItem('user_id') === transferDetail?.member_id;
  }, [transferDetail]);

  const handleSave = useCallback(async () => {
    if (!id || !editData.status_id || editData.comment === undefined) {
      return;
    }

    // If status is Paused and we have a pending pause date, pause first, then update
    if (
      editData.status_id === '35005181463e47b18c000565577cbacf' &&
      pendingPauseDate
    ) {
      try {
        // First, pause the transfer (this will set status to Paused)
        await pauseTransferRequest(id, { paused_until_date: pendingPauseDate });

        // Then update with the comment (status is already Paused, so this just updates comment)
        const updateData: SeekingTransferUpdate = {
          status_id: editData.status_id,
          comment: editData.comment,
        };
        updateTransfer({ id, data: updateData });

        // Clear pending pause date and close edit mode
        setPendingPauseDate(null);
        setIsEditing(false);
      } catch (error) {
        console.error('Failed to pause transfer:', error);
        setErrorMessage('Failed to pause transfer. Please try again.');
      }
    } else if (
      editData.status_id === '35005181463e47b18c000565577cbacf' &&
      !pendingPauseDate
    ) {
      // Status is Paused but no date selected - show error
      setErrorMessage(
        'Please select a pause date by clicking on the Paused status again.'
      );
    } else {
      // Normal update (not pausing)
      const updateData: SeekingTransferUpdate = {
        status_id: editData.status_id,
        comment: editData.comment,
      };
      updateTransfer({ id, data: updateData });
    }
  }, [id, editData, pendingPauseDate, updateTransfer]);

  const handleAddAttempt = useCallback(() => {
    if (id && selectedChapterForAttempt && attemptDate) {
      // Always require a note for all attempts (reached or not reached)
      const chapter = transferDetail?.local_chapters.find(
        (ch) => ch.id === selectedChapterForAttempt
      );
      if (!chapter || !chapter.notes || chapter.notes.length === 0) {
        // No notes exist, open note dialog first
        setSelectedChapterForNote(selectedChapterForAttempt);
        setPendingContactAttempt({
          chapterId: selectedChapterForAttempt,
          attempt:
            currentAttemptNumber === 1
              ? 'one'
              : currentAttemptNumber === 2
                ? 'two'
                : 'three',
          date: attemptDate,
          successful: attemptSuccessful,
        });
        // Force note type to 'canned' for attempt notes (Other option is not available)
        setNoteType('canned');
        setSelectedCannedNote(null);
        setOtherNoteText('');
        // Auto-select note type filter based on contact status
        if (attemptSuccessful) {
          setSelectedCannedNoteType('0ee4d64c8f7b4493a1d479acb87e989c'); // Contact Made
        } else {
          setSelectedCannedNoteType('83c8f9dcae514b5f9b5ebd887ee451d1'); // No Contact Made
        }
        setShowNoteDialog(true);
        return;
      }

      // If note exists, proceed with attempt update
      const updateData: SeekingTransferChapterUpdate = {};
      if (currentAttemptNumber === 1) {
        updateData.contact_attempt_one = attemptDate;
        updateData.contact_one_successful = attemptSuccessful;
      } else if (currentAttemptNumber === 2) {
        updateData.contact_attempt_two = attemptDate;
        updateData.contact_two_successful = attemptSuccessful;
      } else if (currentAttemptNumber === 3) {
        updateData.contact_attempt_three = attemptDate;
        updateData.contact_three_successful = attemptSuccessful;
      }

      updateChapter({
        id,
        chapterId: selectedChapterForAttempt,
        data: updateData,
      });
      setAttemptDate('');
      setAttemptSuccessful(false);
      setSelectedChapterForAttempt(null);
      setIsAddingAttempt(false);
    }
  }, [
    id,
    selectedChapterForAttempt,
    attemptDate,
    attemptSuccessful,
    currentAttemptNumber,
    updateChapter,
    transferDetail,
  ]);

  const handleAddChapter = useCallback(
    (chapterId: string) => {
      if (id && chapterId) {
        addChapter({ chapter_id: chapterId });
      }
    },
    [id, addChapter]
  );

  const handleTransferConfirm = useCallback(() => {
    if (id && selectedChapterForAction && transferDate) {
      transferChapter({
        id,
        chapterId: selectedChapterForAction,
        data: { transfer_date: transferDate },
      });
    }
  }, [id, selectedChapterForAction, transferDate, transferChapter]);

  const handleRemoveConfirm = useCallback(() => {
    if (id && selectedChapterForAction) {
      updateChapter({
        id,
        chapterId: selectedChapterForAction,
        data: { close_chapter_request: true },
      });
      setShowRemoveConfirm(false);
      setSelectedChapterForAction('');
    }
  }, [id, selectedChapterForAction, updateChapter]);

  const handleReopenChapter = useCallback(
    (chapterId: string) => {
      if (id) {
        reopenChapter({ id, chapterId });
      }
    },
    [id, reopenChapter]
  );

  // Check if transfer request is paused
  const isPaused = useCallback(() => {
    if (!transferDetail?.paused_until) return false;
    const pausedUntil = dayjs(transferDetail.paused_until);
    return pausedUntil.isAfter(dayjs());
  }, [transferDetail]);

  const handlePause = useCallback(() => {
    setIsPauseFromEdit(false); // Standalone pause, not from edit mode
    setShowPauseDialog(true);
  }, []);

  const handleUnpause = useCallback(() => {
    if (id) {
      unpauseTransfer({ id });
    }
  }, [id, unpauseTransfer]);

  const handlePauseConfirm = useCallback(() => {
    if (!id) return;

    let pausedUntilDate: string;
    if (pauseDuration === 'other' && pauseCustomDate) {
      pausedUntilDate = pauseCustomDate.format('YYYY-MM-DD');
    } else {
      const days = parseInt(pauseDuration);
      pausedUntilDate = dayjs().add(days, 'days').format('YYYY-MM-DD');
    }

    // If opened from edit mode, store the date instead of pausing immediately
    if (isPauseFromEdit) {
      setPendingPauseDate(pausedUntilDate);
      setShowPauseDialog(false);
      setPauseDuration('30');
      setPauseCustomDate(null);
      setIsPauseFromEdit(false);
    } else {
      // Standalone pause - pause immediately
      pauseTransfer({
        id,
        data: { paused_until_date: pausedUntilDate },
      });
    }
  }, [id, pauseDuration, pauseCustomDate, pauseTransfer, isPauseFromEdit]);

  const handleOpenNoteDialog = useCallback((chapterId: string) => {
    setSelectedChapterForNote(chapterId);
    // Preselect 'General' filter when opening from Add Note button (not from attempt)
    setSelectedCannedNoteType('1c7be7c1a3e841cfbac8e9d783c32d84'); // General
    setShowNoteDialog(true);
  }, []);

  const handleCloseNoteDialog = useCallback(() => {
    setShowNoteDialog(false);
    setSelectedChapterForNote(null);
    setSelectedCannedNote(null);
    setNoteType('canned');
    setOtherNoteText('');
    setSelectedCannedNoteType('all');
    setPendingContactAttempt(null);
  }, []);

  const handleCreateNote = useCallback(() => {
    if (!id || !selectedChapterForNote) return;

    // Prevent 'other' note type when opened from attempt context
    if (pendingContactAttempt && noteType === 'other') {
      setErrorMessage(
        'Other notes are not allowed for contact attempts. Please select a standard note.'
      );
      return;
    }

    const noteData: CreateNoteRequest = {
      transfer_chapter_id: selectedChapterForNote,
    };

    if (noteType === 'canned' && selectedCannedNote) {
      noteData.canned_note_id = selectedCannedNote;
    } else if (noteType === 'other' && otherNoteText.trim()) {
      noteData.note = otherNoteText.trim();
    } else {
      setErrorMessage('Please select a standard note or enter a note.');
      return;
    }

    createNoteMutation({ id, data: noteData });
  }, [
    id,
    selectedChapterForNote,
    noteType,
    selectedCannedNote,
    otherNoteText,
    pendingContactAttempt,
    createNoteMutation,
  ]);

  // Load canned notes when dialog opens
  React.useEffect(() => {
    if (showNoteDialog) {
      getCannedNotes()
        .then((response) => {
          setCannedNotes(response.canned_notes);
        })
        .catch((error) => {
          console.error('Error loading canned notes:', error);
          setErrorMessage('Failed to load canned notes.');
        });
    } else {
      // Reset note dialog state when closed
      setSelectedCannedNoteType('all');
    }
  }, [showNoteDialog]);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Not scheduled';
    return dayjs(dateString).format('MMM DD, YYYY');
  };

  const getStatusColor = (
    statusId: string
  ):
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning' => {
    const statusOption = STATUS_OPTIONS.find((s) => s.value === statusId);
    if (!statusOption) return 'default';

    // Map 'danger' to 'error' since Material-UI doesn't have 'danger'
    if (statusOption.color === 'danger') return 'error';
    return (statusOption.color as any) || 'default';
  };

  const getChapterStatusColor = (
    statusId: string
  ):
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning' => {
    const statusOption = CHAPTER_STATUS_OPTIONS.find(
      (s) => s.value === statusId
    );
    if (!statusOption) return 'default';

    // Map 'danger' to 'error' since Material-UI doesn't have 'danger'
    if (statusOption.color === 'danger') return 'error';
    return (statusOption.color as any) || 'default';
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!transferDetail) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <AlertItem
          severity="error"
          description="Transfer request not found."
          onClose={() => {}}
        />
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate('/seeking-transfer')}
              sx={{ cursor: 'pointer' }}
            >
              Seeking Transfers
            </Link>
            <Typography
              color="primary.main"
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              onClick={() =>
                window.open(`/user/${transferDetail.member_id}`, '_blank')
              }
            >
              {transferDetail.member_name}
            </Typography>
          </Breadcrumbs>

          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography
                variant="h4"
                component="h1"
                color="primary"
                sx={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
                onClick={() =>
                  window.open(`/user/${transferDetail.member_id}`, '_blank')
                }
              >
                {transferDetail.member_name}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: { xs: 'left', md: 'right' } }}
            >
              <Stack
                direction="row"
                spacing={1}
                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
              >
                {!isEditing && !isPaused() && transferDetail?.can_edit && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<PauseIcon />}
                    onClick={handlePause}
                    disabled={isPausing}
                  >
                    Pause
                  </Button>
                )}
                {!isEditing && transferDetail?.can_edit && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    disabled={isUpdating}
                  >
                    Edit
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Messages */}
        {errorMessage && (
          <AlertItem
            severity="error"
            description={errorMessage}
            onClose={() => setErrorMessage('')}
          />
        )}
        {successMessage && (
          <AlertItem
            severity="success"
            description={successMessage}
            onClose={() => setSuccessMessage('')}
          />
        )}

        {/* Pause Indicator */}
        {isPaused() && transferDetail.paused_until && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity="info"
              action={
                transferDetail.can_edit && (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={handleUnpause}
                    disabled={isUnpausing}
                    startIcon={<PlayArrowIcon />}
                  >
                    Unpause
                  </Button>
                )
              }
            >
              Transfer request is paused until{' '}
              {dayjs(transferDetail.paused_until).format('MMM DD, YYYY')}.
              Actions are disabled except for adding notes.
            </Alert>
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Basic Information or Edit Form */}
          <Grid item xs={12} lg={4}>
            {isEditing ? (
              <Card elevation={2}>
                <CardHeader title="Edit Transfer Request" />
                <CardContent>
                  <Stack spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        key={`status-select-${editData.status_id || 'empty'}`}
                        value={editData.status_id || ''}
                        onChange={(e) => {
                          const newStatusId = e.target.value;
                          // If Paused status is selected, open pause dialog to get date
                          if (
                            newStatusId === '35005181463e47b18c000565577cbacf'
                          ) {
                            setIsPauseFromEdit(true); // Mark that this is from edit mode
                            setShowPauseDialog(true);
                            // Store the status change - pause date will be captured in dialog
                            setEditData({
                              ...editData,
                              status_id: newStatusId,
                            });
                          } else {
                            // Clear pending pause date if status changed away from Paused
                            if (
                              editData.status_id ===
                              '35005181463e47b18c000565577cbacf'
                            ) {
                              setPendingPauseDate(null);
                            }
                            setEditData({
                              ...editData,
                              status_id: newStatusId,
                            });
                          }
                        }}
                        label="Status"
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {editData.status_id ===
                      '35005181463e47b18c000565577cbacf' &&
                      pendingPauseDate && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Transfer will be paused until{' '}
                          {dayjs(pendingPauseDate).format('MMM DD, YYYY')}
                        </Alert>
                      )}
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Comments"
                      value={editData.comment || ''}
                      onChange={(e) => {
                        setEditData({ ...editData, comment: e.target.value });
                      }}
                    />
                  </Stack>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={isUpdating}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                </CardActions>
              </Card>
            ) : (
              <Card elevation={2}>
                <CardHeader
                  title="Basic Information"
                  avatar={<PersonIcon color="primary" />}
                />
                <CardContent>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label={transferDetail.status}
                        color={getStatusColor(transferDetail.status_id)}
                        size="small"
                        sx={{ mt: 0.5, ml: 0.5 }}
                      />
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Member
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: 'pointer',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                        onClick={() =>
                          window.open(
                            `/user/${transferDetail.member_id}`,
                            '_blank'
                          )
                        }
                      >
                        {transferDetail.member_name}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body2">
                        {transferDetail.member_email}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body2">
                        {transferDetail.member_phone}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Created By
                      </Typography>
                      <Typography variant="body2">
                        {transferDetail.created_by}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Created Date
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(transferDetail.created_at)}
                      </Typography>
                    </Box>

                    {transferDetail.comment && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Comments
                        </Typography>
                        <Typography variant="body2">
                          {transferDetail.comment}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Right Column - Chapters and Notes */}
          <Grid item xs={12} lg={8}>
            {/* Chapters Section */}
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardHeader
                title="Chapters"
                avatar={<LocationOnIcon color="primary" />}
                action={
                  transferDetail.status_id ===
                    '60baf6c89e6f4343900e4dba8188548d' &&
                  !isPaused() &&
                  transferDetail.can_edit && (
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => setIsAddingChapter(true)}
                      size="small"
                      disabled={isPaused()}
                    >
                      Add Chapter
                    </Button>
                  )
                }
              />
              <CardContent>
                {transferDetail.local_chapters.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    No chapters assigned to this transfer request.
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {transferDetail.local_chapters.map((chapter) => (
                      <Paper key={chapter.id} variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={2}>
                          {/* Chapter Header */}
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              {chapter.name}
                            </Typography>
                            <Chip
                              label={chapter.status}
                              color={getChapterStatusColor(chapter.status_id)}
                              size="small"
                            />
                            {/* Show removed by info for removed chapters */}
                            {chapter.status_id ===
                              '6190df3b5f14416ca50a04c8ee443026' && (
                              <Box sx={{ ml: 2, mt: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {chapter.removed_by_name && (
                                    <>Removed by {chapter.removed_by_name}</>
                                  )}
                                  {chapter.removed_on && (
                                    <>
                                      {chapter.removed_by_name
                                        ? ' on '
                                        : 'Removed on '}
                                      {formatDate(chapter.removed_on)}
                                    </>
                                  )}
                                </Typography>
                              </Box>
                            )}
                            {/* Reopen button for removed chapters - only show if current user removed it */}
                            {chapter.status_id ===
                              '6190df3b5f14416ca50a04c8ee443026' &&
                              transferDetail.status_id ===
                                '60baf6c89e6f4343900e4dba8188548d' &&
                              !isSelf() &&
                              chapter.removed_by_id ===
                                localStorage.getItem('user_id') && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  startIcon={<AddIcon />}
                                  onClick={() =>
                                    handleReopenChapter(chapter.id)
                                  }
                                  disabled={isReopeningChapter}
                                  sx={{ ml: 2, mt: 1 }}
                                >
                                  Reopen Chapter
                                </Button>
                              )}
                          </Box>

                          {/* Contact Attempts Section - Only show if chapter is not removed */}
                          {chapter.status_id !==
                            '6190df3b5f14416ca50a04c8ee443026' && (
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 'medium' }}
                              >
                                Contact Attempts:
                              </Typography>
                              <Stack spacing={1}>
                                {/* Attempt One */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    pl: 2,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: '100px' }}
                                  >
                                    Attempt One:
                                  </Typography>
                                  {chapter.contact_attempt_one ? (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {formatDate(
                                          chapter.contact_attempt_one
                                        )}
                                      </Typography>
                                      <Chip
                                        label={
                                          chapter.contact_one_successful
                                            ? 'Reached'
                                            : 'Not Reached'
                                        }
                                        size="small"
                                        color={
                                          chapter.contact_one_successful
                                            ? 'success'
                                            : 'error'
                                        }
                                      />
                                    </Box>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      No attempt recorded
                                    </Typography>
                                  )}
                                </Box>

                                {/* Attempt Two */}
                                {chapter.contact_attempt_one &&
                                  !chapter.contact_one_successful && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        pl: 2,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ minWidth: '100px' }}
                                      >
                                        Attempt Two:
                                      </Typography>
                                      {chapter.contact_attempt_two ? (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            {formatDate(
                                              chapter.contact_attempt_two
                                            )}
                                          </Typography>
                                          <Chip
                                            label={
                                              chapter.contact_two_successful
                                                ? 'Reached'
                                                : 'Not Reached'
                                            }
                                            size="small"
                                            color={
                                              chapter.contact_two_successful
                                                ? 'success'
                                                : 'error'
                                            }
                                          />
                                        </Box>
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          No attempt recorded
                                        </Typography>
                                      )}
                                    </Box>
                                  )}

                                {/* Attempt Three */}
                                {chapter.contact_attempt_two &&
                                  !chapter.contact_one_successful &&
                                  !chapter.contact_two_successful && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        pl: 2,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ minWidth: '100px' }}
                                      >
                                        Attempt Three:
                                      </Typography>
                                      {chapter.contact_attempt_three ? (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            {formatDate(
                                              chapter.contact_attempt_three
                                            )}
                                          </Typography>
                                          <Chip
                                            label={
                                              chapter.contact_three_successful
                                                ? 'Reached'
                                                : 'Not Reached'
                                            }
                                            size="small"
                                            color={
                                              chapter.contact_three_successful
                                                ? 'success'
                                                : 'error'
                                            }
                                          />
                                        </Box>
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          No attempt recorded
                                        </Typography>
                                      )}
                                    </Box>
                                  )}

                                {/* Add Attempt Buttons - Below attempts */}
                                {transferDetail.status_id ===
                                  '60baf6c89e6f4343900e4dba8188548d' &&
                                  chapter.status_id !==
                                    '6190df3b5f14416ca50a04c8ee443026' &&
                                  chapter.status_id !==
                                    '155a713f82854c07819a533a22ebe158' &&
                                  !isSelf() && (
                                    <Box sx={{ pl: 2, pt: 1 }}>
                                      {!chapter.contact_attempt_one && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="primary"
                                          startIcon={<AddIcon />}
                                          onClick={() => {
                                            setSelectedChapterForAttempt(
                                              chapter.id
                                            );
                                            setCurrentAttemptNumber(1);
                                            setAttemptDate('');
                                            setAttemptSuccessful(false);
                                            setIsAddingAttempt(true);
                                          }}
                                          disabled={isPaused()}
                                        >
                                          Add Attempt One
                                        </Button>
                                      )}
                                      {chapter.contact_attempt_one &&
                                        !chapter.contact_one_successful &&
                                        !chapter.contact_attempt_two && (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                              setSelectedChapterForAttempt(
                                                chapter.id
                                              );
                                              setCurrentAttemptNumber(2);
                                              setAttemptDate('');
                                              setAttemptSuccessful(false);
                                              setIsAddingAttempt(true);
                                            }}
                                            disabled={isPaused()}
                                          >
                                            Add Attempt Two
                                          </Button>
                                        )}
                                      {chapter.contact_attempt_two &&
                                        !chapter.contact_one_successful &&
                                        !chapter.contact_two_successful &&
                                        !chapter.contact_attempt_three && (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                              setSelectedChapterForAttempt(
                                                chapter.id
                                              );
                                              setCurrentAttemptNumber(3);
                                              setAttemptDate('');
                                              setAttemptSuccessful(false);
                                              setIsAddingAttempt(true);
                                            }}
                                            disabled={isPaused()}
                                          >
                                            Add Attempt Three
                                          </Button>
                                        )}
                                    </Box>
                                  )}
                              </Stack>
                            </Box>
                          )}

                          {/* Notes Section */}
                          {chapter.status_id !==
                            '6190df3b5f14416ca50a04c8ee443026' && (
                            <Box
                              sx={{
                                mt: 2,
                                pt: 2,
                                borderTop: 1,
                                borderColor: 'divider',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 'medium' }}
                                >
                                  Notes
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<NoteIcon />}
                                  onClick={() =>
                                    handleOpenNoteDialog(chapter.id)
                                  }
                                  disabled={false}
                                >
                                  Add Note
                                </Button>
                              </Box>
                              {chapter.notes && chapter.notes.length > 0 ? (
                                <Stack spacing={1}>
                                  {chapter.notes.map((note) => (
                                    <Paper
                                      key={note.id}
                                      variant="outlined"
                                      sx={{ p: 1.5 }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'flex-start',
                                          mb: 0.5,
                                        }}
                                      >
                                        <Box sx={{ flex: 1 }}>
                                          {note.canned_note ? (
                                            <Typography variant="body2">
                                              {note.canned_note.note}
                                            </Typography>
                                          ) : (
                                            <Typography variant="body2">
                                              {note.note}
                                            </Typography>
                                          )}
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mt: 0.5, display: 'block' }}
                                          >
                                            {note.created_by_name} -{' '}
                                            {formatDate(note.created_at)}
                                          </Typography>
                                        </Box>
                                        {note.is_private && (
                                          <Chip
                                            label="Private"
                                            size="small"
                                            color="warning"
                                            sx={{ ml: 1 }}
                                          />
                                        )}
                                      </Box>
                                    </Paper>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontStyle: 'italic' }}
                                >
                                  No notes yet
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* Action Buttons - Transfer and Remove */}
                          {transferDetail.status_id ===
                            '60baf6c89e6f4343900e4dba8188548d' &&
                            chapter.status_id !==
                              '6190df3b5f14416ca50a04c8ee443026' &&
                            chapter.status_id !==
                              '155a713f82854c07819a533a22ebe158' && (
                              <Box
                                sx={{
                                  mt: 2,
                                  pt: 2,
                                  borderTop: 1,
                                  borderColor: 'divider',
                                }}
                              >
                                {expandedActionsChapterId === chapter.id ? (
                                  <Box>
                                    <Button
                                      variant="outlined"
                                      color="secondary"
                                      size="small"
                                      startIcon={<ExpandLessIcon />}
                                      onClick={() =>
                                        setExpandedActionsChapterId(null)
                                      }
                                      sx={{ mb: 1 }}
                                      disabled={isPaused()}
                                    >
                                      Hide Actions
                                    </Button>
                                    <Box
                                      sx={{ display: 'flex', gap: 1, mt: 1 }}
                                    >
                                      {(chapter.contact_one_successful ||
                                        chapter.contact_two_successful ||
                                        chapter.contact_three_successful) &&
                                        transferDetail?.can_edit &&
                                        !isSelf() && (
                                          <Button
                                            variant="outlined"
                                            color="secondary"
                                            size="small"
                                            onClick={() => {
                                              if (id) {
                                                undoContact({
                                                  id,
                                                  chapterId: chapter.id,
                                                });
                                              }
                                            }}
                                            disabled={
                                              isUpdatingChapter ||
                                              isUndoingContact ||
                                              isPaused()
                                            }
                                          >
                                            Undo contact made
                                          </Button>
                                        )}
                                      <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<TransferIcon />}
                                        onClick={() => {
                                          setSelectedChapterForAction(
                                            chapter.id
                                          );
                                          setTransferDate(
                                            dayjs().format('YYYY-MM-DD')
                                          );
                                          setShowTransferConfirm(true);
                                        }}
                                        disabled={
                                          isUpdatingChapter ||
                                          isTransferringChapter ||
                                          isSelf() ||
                                          isPaused()
                                        }
                                      >
                                        Transfer
                                      </Button>
                                      <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<CloseIcon />}
                                        onClick={() => {
                                          setSelectedChapterForAction(
                                            chapter.id
                                          );
                                          setShowRemoveConfirm(true);
                                        }}
                                        disabled={
                                          isUpdatingChapter || isPaused()
                                        }
                                      >
                                        Remove
                                      </Button>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    startIcon={<ExpandMoreIcon />}
                                    onClick={() =>
                                      setExpandedActionsChapterId(chapter.id)
                                    }
                                    disabled={isPaused()}
                                  >
                                    Show Actions
                                  </Button>
                                )}
                              </Box>
                            )}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Unified Add Attempt Dialog */}
        <Dialog
          open={isAddingAttempt}
          onClose={() => {
            setIsAddingAttempt(false);
            setSelectedChapterForAttempt(null);
            setAttemptDate('');
            setAttemptSuccessful(false);
            setPendingContactAttempt(null);
          }}
          maxWidth="sm"
          fullWidth
          disableEnforceFocus
        >
          <DialogTitle>
            Add Contact Attempt{' '}
            {currentAttemptNumber === 1
              ? 'One'
              : currentAttemptNumber === 2
                ? 'Two'
                : 'Three'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                type="date"
                label="Attempt Date"
                value={attemptDate}
                onChange={(e) => setAttemptDate(e.target.value)}
                inputProps={{ max: todayYYYYMMDD }}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={attemptSuccessful}
                    onChange={(e) => setAttemptSuccessful(e.target.checked)}
                  />
                }
                label="Were they able to be reached?"
              />
              <Alert severity="info">
                A note is required for all contact attempts. If no note exists
                for this chapter, you will be prompted to add one.
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setIsAddingAttempt(false);
                setSelectedChapterForAttempt(null);
                setAttemptDate('');
                setAttemptSuccessful(false);
                setPendingContactAttempt(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddAttempt}
              disabled={!attemptDate || isUpdatingChapter}
            >
              Add Attempt
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Chapter Dialog */}
        <Dialog
          open={isAddingChapter}
          onClose={() => setIsAddingChapter(false)}
          maxWidth="sm"
          fullWidth
          disableEnforceFocus
        >
          <DialogTitle>Add Chapter to Transfer Request</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <ChapterSelector
                chapter={selectedChapterToAdd}
                handleChapterSelect={(chapterId) =>
                  setSelectedChapterToAdd(chapterId || '')
                }
                spd=""
                all={true}
                reportId="add-chapter-dialog"
                label="Select Chapter"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setIsAddingChapter(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAddChapter(selectedChapterToAdd)}
              disabled={!selectedChapterToAdd || isAddingChapterPending}
            >
              Add Chapter
            </Button>
          </DialogActions>
        </Dialog>

        {/* Transfer Confirmation Dialog */}
        <Dialog
          open={showTransferConfirm}
          onClose={() => setShowTransferConfirm(false)}
          maxWidth="sm"
          fullWidth
          disableEnforceFocus
        >
          <DialogTitle>Transfer Chapter</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Are you sure you want to transfer this chapter? This action
                cannot be undone.
              </Typography>

              <TextField
                fullWidth
                type="date"
                label="Transfer Date"
                value={transferDate}
                onChange={(e) => setTransferDate(e.target.value)}
                inputProps={{ max: todayYYYYMMDD }}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setShowTransferConfirm(false);
                setTransferDate('');
                setSelectedChapterForAction('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleTransferConfirm}
              disabled={!transferDate || isTransferringChapter}
            >
              Transfer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Remove Confirmation Dialog */}
        <ConfirmationDialog
          open={showRemoveConfirm}
          handleCancel={() => setShowRemoveConfirm(false)}
          handleOk={handleRemoveConfirm}
          title="Confirm Removal"
          message="Are you sure you want to remove this chapter from the transfer request? This action cannot be undone."
          confirmText="Remove"
          cancelText="Cancel"
        />

        {/* Pause Dialog */}
        <Dialog
          open={showPauseDialog}
          onClose={() => {
            setShowPauseDialog(false);
            setPauseDuration('30');
            setPauseCustomDate(null);
            // If canceling from edit mode, clear the pending pause and reset status
            if (isPauseFromEdit) {
              setIsPauseFromEdit(false);
              // Reset status back to original if canceling pause dialog
              if (transferDetail) {
                setEditData({
                  ...editData,
                  status_id: transferDetail.status_id,
                });
              }
              setPendingPauseDate(null);
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Pause Transfer Request</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Select how long you want to pause this transfer request. During
                this time, chapters will not be able to take action on this
                request.
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={pauseDuration}
                  onChange={(e) =>
                    setPauseDuration(
                      e.target.value as '30' | '60' | '90' | 'other'
                    )
                  }
                >
                  <FormControlLabel
                    value="30"
                    control={<Radio />}
                    label="30 days"
                  />
                  <FormControlLabel
                    value="60"
                    control={<Radio />}
                    label="60 days"
                  />
                  <FormControlLabel
                    value="90"
                    control={<Radio />}
                    label="90 days"
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio />}
                    label="Other (select date)"
                  />
                </RadioGroup>
              </FormControl>
              {pauseDuration === 'other' && (
                <DatePicker
                  label="Pause Until Date"
                  value={pauseCustomDate}
                  onChange={(newValue) => setPauseCustomDate(newValue)}
                  minDate={dayjs().add(1, 'day')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setShowPauseDialog(false);
                setPauseDuration('30');
                setPauseCustomDate(null);
                // If canceling from edit mode, clear the pending pause and reset status
                if (isPauseFromEdit) {
                  setIsPauseFromEdit(false);
                  // Reset status back to original if canceling pause dialog
                  if (transferDetail) {
                    setEditData({
                      ...editData,
                      status_id: transferDetail.status_id,
                    });
                  }
                  setPendingPauseDate(null);
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handlePauseConfirm}
              disabled={
                isPausing || (pauseDuration === 'other' && !pauseCustomDate)
              }
            >
              Pause
            </Button>
          </DialogActions>
        </Dialog>

        {/* Note Dialog */}
        <Dialog
          open={showNoteDialog}
          onClose={handleCloseNoteDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Add Note</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {pendingContactAttempt && (
                <Alert severity="warning">
                  A note is required before you can mark this contact attempt as
                  successful. Please add a note below.
                </Alert>
              )}
              {/* Step 1: Select note type (canned or other) */}
              <FormControl component="fieldset">
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Note Type
                </Typography>
                <RadioGroup
                  value={noteType}
                  onChange={(e) => {
                    setNoteType(e.target.value as 'canned' | 'other');
                    setSelectedCannedNote(null);
                    setOtherNoteText('');
                  }}
                >
                  <FormControlLabel
                    value="canned"
                    control={<Radio />}
                    label="Select from Standard Notes"
                  />
                  {/* Only show "Other" option when not from attempt context */}
                  {!pendingContactAttempt && (
                    <FormControlLabel
                      value="other"
                      control={<Radio />}
                      label="Other (Private Note)"
                    />
                  )}
                </RadioGroup>
              </FormControl>

              {noteType === 'canned' && (
                <>
                  {/* Filter by type */}
                  <FormControl fullWidth>
                    <InputLabel>Filter by Type</InputLabel>
                    <Select
                      value={selectedCannedNoteType}
                      onChange={(e) =>
                        setSelectedCannedNoteType(e.target.value)
                      }
                      label="Filter by Type"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="0ee4d64c8f7b4493a1d479acb87e989c">
                        Contact Made
                      </MenuItem>
                      <MenuItem value="1c7be7c1a3e841cfbac8e9d783c32d84">
                        General
                      </MenuItem>
                      <MenuItem value="83c8f9dcae514b5f9b5ebd887ee451d1">
                        No Contact Made
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Canned notes list */}
                  <FormControl fullWidth>
                    <InputLabel>Select Standard Note</InputLabel>
                    <Select
                      value={selectedCannedNote || ''}
                      onChange={(e) => setSelectedCannedNote(e.target.value)}
                      label="Select Standard Note"
                    >
                      {cannedNotes
                        .filter((note) =>
                          selectedCannedNoteType === 'all'
                            ? true
                            : note.canned_note_type_id ===
                              selectedCannedNoteType
                        )
                        .map((note) => (
                          <MenuItem key={note.id} value={note.id}>
                            {note.note}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {noteType === 'other' && (
                <>
                  <Alert severity="warning">
                    This note will only be visible to you. Other users will not
                    be able to see this note.
                  </Alert>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Note"
                    value={otherNoteText}
                    onChange={(e) => setOtherNoteText(e.target.value)}
                    placeholder="Enter your note here..."
                    required
                  />
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCloseNoteDialog}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateNote}
              disabled={
                isCreatingNote ||
                (noteType === 'canned' && !selectedCannedNote) ||
                (noteType === 'other' && !otherNoteText.trim())
              }
            >
              Add Note
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
}
